<script setup>
import { computed, ref, onMounted, nextTick, watch } from 'vue';
import { useResizeObserver } from '@vueuse/core';

const props = defineProps({
  initialActiveTab: {
    type: Number,
    default: 0,
  },
  tabs: {
    type: Array,
    required: true,
    validator: value => {
      return value.every(
        tab =>
          typeof tab.label === 'string' &&
          (tab.count ? typeof tab.count === 'number' : true)
      );
    },
  },
});

const emit = defineEmits(['tabChanged']);

const activeTab = computed(() => props.initialActiveTab);

const tabRefs = ref([]);
const indicatorStyle = ref({});
const enableTransition = ref(false);

const activeElement = computed(() => tabRefs.value[activeTab.value]);

const updateIndicator = () => {
  nextTick(() => {
    if (!activeElement.value) return;

    indicatorStyle.value = {
      left: `${activeElement.value.offsetLeft}px`,
      width: `${activeElement.value.offsetWidth}px`,
    };
  });
};

useResizeObserver(activeElement, updateIndicator);

// Watch for prop/tabs changes to update indicator position
watch([() => props.initialActiveTab, () => props.tabs], updateIndicator, {
  immediate: true,
});

onMounted(() => {
  nextTick(() => {
    enableTransition.value = true;
  });
});

const selectTab = index => {
  emit('tabChanged', props.tabs[index]);
};
</script>

<template>
  <div
    class="relative flex w-fit max-w-full items-center gap-0.5 overflow-x-auto rounded-xl border border-n-weak bg-n-alpha-1 p-1 dark:bg-n-solid-1"
    role="tablist"
  >
    <div
      class="pointer-events-none absolute inset-y-1 rounded-lg bg-n-solid-1 shadow-sm ring-1 ring-n-container dark:bg-n-solid-3"
      :class="{ 'transition-all duration-300 ease-out': enableTransition }"
      :style="indicatorStyle"
    />

    <button
      v-for="(tab, index) in tabs"
      :key="index"
      :ref="el => (tabRefs[index] = el)"
      type="button"
      role="tab"
      :aria-selected="activeTab === index"
      class="relative z-10 shrink-0 truncate rounded-lg border-0 px-4 py-1.5 text-sm font-medium outline-none transition-colors duration-200 ease-out focus-visible:ring-2 focus-visible:ring-n-brand/40"
      :class="
        activeTab === index
          ? 'text-n-brand'
          : 'text-n-slate-11 hover:text-n-slate-12'
      "
      @click="selectTab(index)"
    >
      {{ tab.label }} {{ tab.count ? `(${tab.count})` : '' }}
    </button>
  </div>
</template>
